import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Brain, Heart, Zap, Clock } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function PositiveExperiencesStick() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const keyPoints = [
    {
      title: 'The Negativity Bias',
      content: 'Our brains are wired to notice and remember negative experiences more than positive ones. This "negativity bias" evolved to keep us safe from threats.',
      icon: '🧠'
    },
    {
      title: 'Teflon for Good, Velcro for Bad',
      content: 'Positive experiences tend to slide off like teflon, while negative experiences stick like velcro. We must intentionally work to absorb the good.',
      icon: '🎯'
    },
    {
      title: 'The 3:1 Ratio',
      content: 'Research suggests we need about 3 positive experiences to counterbalance 1 negative experience. This is why actively cultivating positivity matters.',
      icon: '⚖️'
    }
  ];

  const practices = [
    {
      title: 'HEAL Practice',
      steps: [
        { letter: 'H', word: 'Have', description: 'Notice or create a positive experience' },
        { letter: 'E', word: 'Enrich', description: 'Stay with it for 20-30 seconds; intensify it' },
        { letter: 'A', word: 'Absorb', description: 'Intend and sense it sinking into you' },
        { letter: 'L', word: 'Link', description: 'Connect the positive to negative material (optional)' }
      ]
    }
  ];

  const everydayOpportunities = [
    'A warm cup of tea or coffee',
    'A moment of genuine connection',
    'Completing a task',
    'Receiving a kind word',
    'Enjoying a beautiful sight',
    'Feeling safe and warm',
    'A moment of laughter',
    'Physical comfort'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Workbook
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Do Positive Experiences "Stick To Your Ribs?"</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 55</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Brain className="h-16 w-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Do Positive Experiences "Stick To Your Ribs?"</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">Understanding how our brains process positive experiences—and how to make them last.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {keyPoints.map((point, index) => (
              <div key={index} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
                <div className="text-4xl mb-3">{point.icon}</div>
                <h4 className="text-lg font-bold text-amber-400 mb-2">{point.title}</h4>
                <p className="text-slate-300 text-sm">{point.content}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/30 rounded-lg p-8 mb-10">
            <h3 className="text-2xl font-bold text-green-200 mb-6 flex items-center gap-2">
              <Heart className="h-6 w-6" /> The HEAL Practice
            </h3>
            <p className="text-green-100 mb-6">Developed by Dr. Rick Hanson, HEAL helps us "install" positive experiences into our neural structure.</p>
            <div className="grid md:grid-cols-4 gap-4">
              {practices[0].steps.map((step, index) => (
                <div key={index} className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-white">{step.letter}</span>
                  </div>
                  <h4 className="font-bold text-green-200 mb-1">{step.word}</h4>
                  <p className="text-slate-300 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-amber-200 mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5" /> The 20-30 Second Rule
            </h3>
            <p className="text-amber-100 mb-4">It takes about 20-30 seconds to move an experience from short-term to long-term memory. When something good happens:</p>
            <div className="bg-slate-900/50 rounded-lg p-4 flex items-center gap-4">
              <Clock className="h-10 w-10 text-amber-400" />
              <div>
                <p className="text-white font-medium">Stay with it for at least 20-30 seconds</p>
                <p className="text-slate-300 text-sm">Feel it in your body. Let it really sink in. This simple practice rewires your brain over time.</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-purple-200 mb-4">Everyday Opportunities to "Install" the Good</h3>
            <div className="grid md:grid-cols-4 gap-3">
              {everydayOpportunities.map((opp, index) => (
                <div key={index} className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-purple-100 text-sm">{opp}</p>
                </div>
              ))}
            </div>
          </div>

          <PracticeScenario pageTitle="Positive Experiences" pageNumber={55} />
          <PageMessaging pageTitle="Positive Experiences" pageNumber={55} />
        </div>
      </div>
    </div>
  );
}
